<template>
    <component
        :is="stoneRoute ? 'RouterLink' : 'div'"
        v-qa="'stone-item-preview'"
        :to="stoneRoute"
        class="stone-preview"
    >
        <div
            class="image-wrapper"
            :class="imageWrapperClasses"
        >
            <img
                v-if="image && 'url' in image"
                :alt="imageAlt"
                :src="image.url"
                class="image"
                loading="lazy"
            />

            <Image
                v-else-if="image && 'image' in image"
                class="image"
                loading="lazy"
                :alt="imageAlt"
                v-bind="image.image"
            />

            <ExpressDeliveryIcon
                v-if="isExpress"
                class="express-delivery-icon"
            />
        </div>

        <div class="details">
            <div class="top-row">
                <h3 class="title">{{ title }}</h3>

                <div
                    v-if="totalSaleAmount.amount"
                    class="price"
                >
                    <span v-if="0 === Number(totalSaleAmount.amount)">
                        {{ t('diamond_search.enquire_for_price') }}
                    </span>

                    <Money :data="totalSaleAmount" />
                </div>
            </div>

            <div class="subtitle">
                {{ subtitle }}

                <div
                    v-if="consultantOnlyInfo.length"
                    v-qa="'consultant-only-info'"
                >
                    <div
                        v-for="info in consultantOnlyInfo"
                        :key="info"
                    >
                        {{ info }}
                    </div>
                </div>
            </div>

            <slot name="details"></slot>

            <div class="bottom-row">
                <div
                    v-if="!$slots['button']"
                    class="more-info"
                >
                    More info +
                </div>

                <div
                    v-if="certificate"
                    class="certificate"
                >
                    {{ certificate?.lab }}
                </div>

                <div
                    v-if="$slots['button']"
                    class="button-wrapper"
                >
                    <slot name="button"></slot>
                </div>
            </div>
        </div>
    </component>
</template>

<script lang="ts" setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import { useTotalSaleAmount } from '@/domain/stone/price'
import { useStoneImage } from '@/domain/stone/useStoneImage'
import { assertNever } from '@/helpers'
import type { ButtonRouteRelative } from '@/components/ui/buttons/useThButton'
import type { RingBuilderStoneTypes } from '@/domain/product/ring/ring-builder'
import {
    getStoneSubtitle,
    getStoneTitle,
    getGemsApiStoneConsultantInfo,
    getIsExpress,
} from './getStoneTitles'

import { useIsAdmin } from '@/components/app/isAdmin.ts'
import { toCertificate } from '@/domain/stone/certificate'

import Money from '../Money.vue'
import Image from '@/components/Image.vue'
import ExpressDeliveryIcon from './search/sku/ExpressDeliveryIcon.vue'

const { stone, stoneRoute = undefined } = defineProps<{
    stone: RingBuilderStoneTypes
    stoneRoute?: ButtonRouteRelative
}>()

const { t } = useI18n()
const isAdmin = useIsAdmin()

const image = useStoneImage(computed(() => stone))

const imageAlt = computed(() => {
    let description = ''

    switch (stone.__typename) {
        case 'NonSkuGemstone':
        // fallthrough
        case 'NonSkuLabGrown':
            description = stone.shape.translation.name
            break
        case 'GemsApi_Stone':
            description = `${
                stone.shapeWithInfo.info?.translation.name ||
                stone.shapeWithInfo.code
            } ${
                stone.categoryWithInfo.info?.translation.name ||
                stone.categoryWithInfo.code
            }`
            break
        default:
            assertNever(stone, '')
    }

    return `${description} image`
})

const title = computed(() => getStoneTitle(stone))
const subtitle = computed(() => getStoneSubtitle(stone))
const consultantOnlyInfo = computed(() =>
    getGemsApiStoneConsultantInfo(stone, isAdmin.value)
)
const isExpress = computed(() => getIsExpress(stone))

const { totalSaleAmount } = useTotalSaleAmount(computed(() => stone))

const imageWrapperClasses = computed((): Record<string, boolean> => {
    if (!isAdmin.value) {
        return {}
    }

    if (stone.__typename === 'GemsApi_Stone') {
        return {
            red: stone.isB2B || !stone.isValidB2C,
        }
    }

    return {}
})

const stoneWithCertificate = computed(() =>
    stone.__typename === 'GemsApi_Stone' ? stone : null
)
const certificate = toCertificate(stoneWithCertificate)
</script>

<style lang="scss" scoped>
.stone-preview {
    cursor: pointer;

    .image-wrapper {
        position: relative;
        width: 100%;
        aspect-ratio: 1 / 1;

        &.red {
            border: 1px solid $red;
        }

        .image {
            width: 100%;
            height: 100%;
            object-fit: cover;
        }

        .express-delivery-icon {
            position: absolute;
            top: 10px;
            right: 10px;
        }
    }

    .details {
        width: 100%;
        display: flex;
        flex-wrap: wrap;
        padding: 12px;
        align-self: flex-end;

        .top-row {
            margin-bottom: 5px;
            width: 100%;
            display: flex;
            align-items: center;
            justify-content: space-around;

            @include min-width(640) {
                margin-bottom: 10px;
            }

            .title {
                font-family: $parcel-font-stack;
                letter-spacing: 0.01em;
                font-size: 1rem;
                line-height: 1em;
                width: 100%;

                @include min-width(640) {
                    font-size: 1.125rem;
                }
            }

            .price {
                font-family: $effra-font-stack;
                color: $slate;
                text-transform: uppercase;
                font-size: 15px;
                font-weight: 400;
                line-height: 18.75px;
                letter-spacing: 0.01em;
            }
        }

        .subtitle {
            width: 100%;
            margin-bottom: 20px;
            font-family: $effra-font-stack;

            font-size: 14px;
            font-weight: 400;
            line-height: 17.5px;
            letter-spacing: 0.01em;
            color: $grey-500;

            @include min-width(640) {
                font-size: 0.875rem;
                margin-bottom: 20px;
            }
        }

        .bottom-row {
            width: 100%;
            display: flex;
            align-items: center;
            justify-content: space-between;

            font-family: $effra-font-stack;
            text-transform: uppercase;
            font-size: 11px;
            font-weight: 400;
            line-height: 17.5px;
            letter-spacing: 0.01em;
            color: $grey-500;

            .certificate {
                position: relative;
                margin-left: 12px;

                &::before {
                    position: absolute;
                    top: calc(50% - 4.5px);
                    left: -11px;
                    width: 9px;
                    height: 9px;
                    content: '';
                    border-radius: 50%;
                    border: 1px solid $grey-500;
                }
            }

            .button-wrapper {
                width: 100%;
                display: flex;
                justify-content: flex-end;
            }
        }
    }
}
</style>
